/**
 * Single source of truth for how a worklist "kind" presents across the app — its icon, its semantic tone
 * (the same StatusPill vocabulary the tables' count pills use), and its i18n label key.
 *
 * Before this file, HomePage and DashboardPage each kept their own icon/tone/medallion maps, and they
 * DRIFTED: "upsell" rendered GREEN + TrendingUp + an "opportunity" framing on Home, but AMBER + Scale +
 * "License discrepancy" on the Dashboard — one list, two colours, two metaphors (r1 critique, 24/7). The
 * Dashboard's reading is the correct one (it IS a mismatch — assigned > purchased — not upward momentum),
 * so that is what survives here. Both screens now import this instead of hand-rolling their own map, so
 * a kind can never again mean two different things depending which route you're on.
 */
import type { ReactNode } from "react";
import { CalendarClock, Gauge, Hourglass, Scale, TrendingDown } from "lucide-react";
import type { WorklistKind } from "@/api/types";
import type { StatusTone } from "@/components/ui";
import { cn } from "@/lib/cn";
import type { TKey } from "@/i18n/keys";

export interface KindPresentation {
  icon: ReactNode;
  /** The list's committed semantic colour — the mapping a CSM lives by: renewal is a calm scheduled
   *  QUEUE (info, not alarm), churn is the only true alarm (danger), upsell/underutilized are attention/
   *  verify (warn), trial_expiring is a conversion window, not a problem (ok) — urgency lives on the
   *  per-row days pill, not here. */
  tone: StatusTone;
  labelKey: TKey;
}

export const KIND_PRESENTATION: Record<WorklistKind, KindPresentation> = {
  renewal: {
    icon: <CalendarClock className="size-5" aria-hidden />,
    tone: "info",
    labelKey: "worklist.renewal",
  },
  churn: {
    icon: <TrendingDown className="size-5" aria-hidden />,
    tone: "danger",
    labelKey: "worklist.churn",
  },
  // "upsell" kind is displayed as the License-discrepancy list (assigned > purchased) — a scales mark
  // reads as a mismatch/imbalance, not an upward opportunity. THE ONE TRUTH — see this file's banner.
  upsell: {
    icon: <Scale className="size-5" aria-hidden />,
    tone: "warn",
    labelKey: "worklist.upsell",
  },
  underutilized: {
    icon: <Gauge className="size-5" aria-hidden />,
    tone: "warn",
    labelKey: "worklist.underutilized",
  },
  // Free trials running out — an hourglass, not a calendar: this is a conversion window, not a renewal.
  trial_expiring: {
    icon: <Hourglass className="size-5" aria-hidden />,
    tone: "ok",
    labelKey: "worklist.trial_expiring",
  },
};

/** Vivid solid fill per tone — only ever applied to a tile that is actually saying something (see
 *  medallionClass below). "neutral" has no vivid form by design: a neutral tone means "nothing to flag",
 *  so it never earns a saturated medallion no matter the count. */
const TONE_SOLID: Record<Exclude<StatusTone, "neutral">, string> = {
  ok: "bg-(--ok-solid)",
  warn: "bg-(--warn-solid)",
  danger: "bg-(--danger-solid)",
  info: "bg-(--info-solid)",
};

/** Quiet resting fill — a tile with nothing (yet) to say: still loading, or a genuine zero. */
const NEUTRAL_MEDALLION = "bg-(--surface-sunken) text-(--text-muted)";

/**
 * The stat-tile medallion's classes: NEUTRAL while pending or sitting at a real zero, and its semantic
 * solid colour ONLY once `active` (count > 0) is true. Colour means a LIVE status, not a permanent
 * category label — a churn tile painted red at count 0 taught the operator to ignore red; this makes red
 * mean something again. A "neutral" tone (e.g. the lapsed-trials prospect tile, deliberately the
 * quietest tile in the band) never turns vivid regardless of `active` — that tile is quiet by design, not
 * by count.
 */
export function medallionClass(tone: StatusTone, active: boolean): string {
  if (!active || tone === "neutral") return NEUTRAL_MEDALLION;
  return cn(TONE_SOLID[tone], "text-(--text-onbrand)");
}
